import { memo } from "react";
import { generatePath, Link } from "react-router";

import { PATHS } from "@/routes/paths";

import { FeatureIslandMain } from "@/components/features/_layouts";

import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { Label } from "@/components/ui/label/label";

import { MaskCreditInfo, PayjpCreditForm } from "./components";

type Props = {
	hotelID: string;
	expired: string;
	last4: string;
	canEdit: boolean;
	onChangeCanEdit: (value: boolean) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSetTargetElement: (element: any) => void;
	onChangeHasPayjpValidation: (hasError: boolean) => void;
	onChangeCardHolderName: (value: string) => void;
	// 宛名関連
	recipientAddressee: string;
	recipientAddresseeErrorMessage: string;
	onChangeRecipientAddressee: (value: string) => void;
};

export const SettingsPaymentCreditMain = memo(
	({
		hotelID,
		expired,
		last4,
		canEdit,
		onChangeCanEdit,
		onChangeHasPayjpValidation,
		onSetTargetElement,
		onChangeCardHolderName,
		recipientAddressee,
		recipientAddresseeErrorMessage,
		onChangeRecipientAddressee,
	}: Props) => {
		const handleChangeCanEdit = () => {
			onChangeCanEdit(!canEdit);
		};

		return (
			<FeatureIslandMain>
				<div>
					<p className="text-sm">基本料金や定額チケットのお支払いに必要です。</p>
				</div>
				{/* クレジットカード設定セクション */}
				<div className="mt-5 rounded-lg border border-zinc-100 bg-white p-4 lg:p-12">
					<div className="space-y-4">
						<div className="flex items-center gap-x-2">
							<h4 className="text-base font-semibold">クレジットカード設定</h4>
							<Label text="必須" size="sm" variant="required" />
						</div>
						<p className="text-sm text-zinc-500">
							基本料金や定額チケットのお支払いに使用するクレジットカードを登録してください。
						</p>
						<div className="relative">
							<div className="flex-1">
								{canEdit ? (
									<PayjpCreditForm
										onChangeHasPayjpValidation={onChangeHasPayjpValidation}
										onSetTargetElement={onSetTargetElement}
										onChangeCardHolderName={onChangeCardHolderName}
									/>
								) : (
									<MaskCreditInfo last4={last4} expired={expired} />
								)}
							</div>
							<div className="absolute right-0 top-0">
								{canEdit ? (
									<img
										onClick={handleChangeCanEdit}
										className="cursor-pointer"
										src="/assets/icon/editor/edit_off.svg"
										alt=""
									/>
								) : (
									<img
										onClick={handleChangeCanEdit}
										className="cursor-pointer"
										src="/assets/icon/editor/create.svg"
										alt=""
									/>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* 宛名セクション */}
				<div className="mt-5 rounded-lg border border-zinc-100 bg-white p-4 lg:p-12">
					<div className="space-y-4">
						<div className="flex items-center gap-x-2">
							<h4 className="text-base font-semibold">領収書の宛名</h4>
							<Label text="必須" size="sm" variant="required" />
						</div>
						<div className="text-sm leading-relaxed text-zinc-500">
							<p>
								領収書に記載する宛名を入力してください。（初期設定は
								<Link
									to={generatePath(PATHS.HOTEL.HOTEL_SETTINGS.EDIT.DEFAULT, { hotelID })}
									className="text-link-400 underline"
								>
									施設情報
								</Link>
								で設定した施設名になります。）
								<br />
								こちらで設定した宛名は、現地決済手数料・CHILLNN利用料金・振込手数料の領収書に使用されます。
								<br />
								※ゲスト向け領収書の発行者名は施設名で固定になります。
							</p>
						</div>
						<div className="w-full md:max-w-md">
							<InputTextField
								placeholder="例：株式会社CHILLNN"
								value={recipientAddressee}
								errorMessage={recipientAddresseeErrorMessage}
								handleChange={onChangeRecipientAddressee}
							/>
						</div>
					</div>
				</div>
			</FeatureIslandMain>
		);
	},
);
